import { useMemo, useRef, useState } from "react";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { useTranslation } from "react-i18next";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { normalizeText } from "utils/text.normalize.logic";
import {
  ActionSlot,
  CheckboxBox,
  CheckboxLabel,
  ClearButton,
  Container,
  DropdownState,
  EmptyState,
  FieldWrapper,
  HiddenInput,
  IconWrapper,
  Input,
  Label,
  List,
  ListScroll,
  OptionLabel,
  OptionRow,
  SelectionBadge,
  TeamsList,
  TooltipAligner,
  TooltipColumn,
  TooltipItem,
  TooltipList,
  UnitBlock,
} from "./unitTeamDropdown.styles";

export interface UnitTeamOption {
  unitId: number;
  unitName: string | null;
  teams: Array<{ id: number; name: string | null }>;
}

interface UnitTeamDropdownProps {
  label: string;
  placeholder: string;
  options: UnitTeamOption[];
  selectedTeamIds: string[];
  onSelectedTeamIdsChange: (values: string[]) => void;
}

const buildSelectedLabel = (
  options: UnitTeamOption[],
  selectedTeamIds: string[],
  translate: (key: string, options?: { defaultValue?: string }) => string,
): {
  displayLabel: string;
  extraCount: number;
  fullLabel: string;
  labels: string[];
} => {
  if (selectedTeamIds.length === 0) {
    return { displayLabel: "", extraCount: 0, fullLabel: "", labels: [] };
  }
  const selectedSet = new Set(selectedTeamIds);
  const labels: string[] = [];
  options.forEach((unit) => {
    const unitName =
      (unit.unitName ?? "").trim() || translate("Schedule.UnknownUnit");
    unit.teams.forEach((team) => {
      if (selectedSet.has(String(team.id))) {
        const teamName =
          (team.name ?? "").trim() || translate("Schedule.UnknownTeam");
        labels.push(`${unitName}/${teamName}`);
      }
    });
  });
  const displayLabels = labels.slice(0, 2);
  const extraCount = Math.max(labels.length - displayLabels.length, 0);
  return {
    displayLabel: displayLabels.join(", "),
    extraCount,
    fullLabel: labels.join(", "),
    labels,
  };
};

const splitLabels = (labels: string[]): [string[], string[]] => {
  if (labels.length <= 6) return [labels, []];
  const mid = Math.ceil(labels.length / 2);
  return [labels.slice(0, mid), labels.slice(mid)];
};

const filterOptions = (
  options: UnitTeamOption[],
  query: string,
  translate: (key: string, options?: { defaultValue?: string }) => string,
): UnitTeamOption[] => {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) return options;
  return options
    .map((unit) => {
      const unitName =
        (unit.unitName ?? "").trim() || translate("Schedule.UnknownUnit");
      const unitMatches = normalizeText(unitName).includes(normalizedQuery);
      if (unitMatches) return unit;
      const teams = unit.teams.filter((team) =>
        normalizeText(
          (team.name ?? "").trim() || translate("Schedule.UnknownTeam"),
        ).includes(normalizedQuery),
      );
      if (teams.length === 0) return null;
      return { ...unit, teams };
    })
    .filter((unit): unit is UnitTeamOption => Boolean(unit));
};

export const UnitTeamDropdown: React.FC<UnitTeamDropdownProps> = (props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedLabel = useMemo(
    () => buildSelectedLabel(props.options, props.selectedTeamIds, t),
    [props.options, props.selectedTeamIds, t],
  );
  const filteredOptions = useMemo(
    () => filterOptions(props.options, query, t),
    [props.options, query, t],
  );

  useOnClickOutside(
    [containerRef],
    () => {
      setIsOpen(false);
      setQuery(selectedLabel.displayLabel);
    },
    { enabled: isOpen, eventType: "mousedown" },
  );

  const selectedValueSet = useMemo(
    () => new Set(props.selectedTeamIds),
    [props.selectedTeamIds],
  );

  const visualState: DropdownState = useMemo(() => {
    if (isOpen) return "active";
    if (props.selectedTeamIds.length > 0) return "filled";
    return "inactive";
  }, [isOpen, props.selectedTeamIds.length]);
  const canClear = props.selectedTeamIds.length > 0;

  const openDropdown = (): void => {
    setIsOpen(true);
    setQuery("");
  };

  const closeDropdown = (): void => {
    setIsOpen(false);
    setQuery(selectedLabel.displayLabel);
  };

  const toggleTeam = (teamId: number): void => {
    const next = new Set(selectedValueSet);
    const value = String(teamId);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    props.onSelectedTeamIdsChange(Array.from(next));
  };

  const toggleUnit = (unit: UnitTeamOption): void => {
    const next = new Set(selectedValueSet);
    const unitTeamIds = unit.teams.map((team) => String(team.id));
    const selectedCount = unitTeamIds.filter((id) => next.has(id)).length;
    const shouldSelectAll = selectedCount !== unitTeamIds.length;
    unitTeamIds.forEach((id) => {
      if (shouldSelectAll) next.add(id);
      else next.delete(id);
    });
    props.onSelectedTeamIdsChange(Array.from(next));
  };

  const resolvedValue = isOpen ? query : selectedLabel.displayLabel;
  const placeholderText = props.placeholder;
  const showExtraCountBadge = !isOpen && selectedLabel.extraCount > 0;

  return (
    <Container ref={containerRef}>
      <Label>{props.label}</Label>
      <FieldWrapper
        $state={visualState}
        onClick={() => {
          if (!isOpen) openDropdown();
        }}
      >
        <Input
          type="text"
          value={resolvedValue}
          placeholder={placeholderText}
          onChange={(event) => {
            if (!isOpen) setIsOpen(true);
            setQuery(event.target.value);
          }}
          onFocus={openDropdown}
        />
        {showExtraCountBadge && (
          <TooltipAligner>
            <TooltipHover
              content={
                <TooltipList $columns={selectedLabel.labels.length > 6 ? 2 : 1}>
                  {splitLabels(selectedLabel.labels).map(
                    (column, columnIndex) => (
                      <TooltipColumn key={`column-${columnIndex}`}>
                        {column.map((label) => (
                          <TooltipItem key={label}>{label}</TooltipItem>
                        ))}
                      </TooltipColumn>
                    ),
                  )}
                </TooltipList>
              }
              arrow="down"
              textAlign="left"
              minWidth={230}
              maxWidth={330}
            >
              <SelectionBadge>+{selectedLabel.extraCount}</SelectionBadge>
            </TooltipHover>
          </TooltipAligner>
        )}
        <ActionSlot>
          {canClear && (
            <ClearButton
              type="button"
              aria-label="Clear selection"
              onClick={(event) => {
                event.stopPropagation();
                props.onSelectedTeamIdsChange([]);
                setQuery("");
              }}
            >
              <CustomIcon name="v2/cross" size="16px" color={ColorV2.Blue5} />
            </ClearButton>
          )}
          <IconWrapper
            type="button"
            aria-label="Toggle dropdown"
            onClick={(event) => {
              event.stopPropagation();
              if (isOpen) closeDropdown();
              else openDropdown();
            }}
          >
            <CustomIcon
              name={isOpen ? "v2/up-chevron" : "v2/down-chevron"}
              size="20px"
              color={ColorV2.Blue5}
            />
          </IconWrapper>
        </ActionSlot>
      </FieldWrapper>
      <List $isOpen={isOpen}>
        <ListScroll>
          {filteredOptions.length === 0 && (
            <EmptyState>{t("Schedule.NoUnitTeamMatches")}</EmptyState>
          )}
          {filteredOptions.map((unit) => {
            const unitName =
              (unit.unitName ?? "").trim() || t("Schedule.UnknownUnit");
            const unitTeamIds = unit.teams.map((team) => String(team.id));
            const selectedCount = unitTeamIds.filter((id) =>
              selectedValueSet.has(id),
            ).length;
            const isChecked =
              unitTeamIds.length > 0 && selectedCount === unitTeamIds.length;
            const isIndeterminate =
              selectedCount > 0 && selectedCount < unitTeamIds.length;
            return (
              <UnitBlock key={unit.unitId}>
                <OptionRow onClick={() => toggleUnit(unit)} role="button">
                  <TriStateCheckbox
                    checked={isChecked}
                    indeterminate={isIndeterminate}
                    onToggle={() => toggleUnit(unit)}
                  />
                  <OptionLabel>{unitName}</OptionLabel>
                </OptionRow>
                <TeamsList>
                  {unit.teams.map((team) => (
                    <OptionRow
                      key={team.id}
                      onClick={() => toggleTeam(team.id)}
                      role="button"
                    >
                      <TriStateCheckbox
                        checked={selectedValueSet.has(String(team.id))}
                        indeterminate={false}
                        onToggle={() => toggleTeam(team.id)}
                      />
                      <OptionLabel>
                        {(team.name ?? "").trim() || t("Schedule.UnknownTeam")}
                      </OptionLabel>
                    </OptionRow>
                  ))}
                </TeamsList>
              </UnitBlock>
            );
          })}
        </ListScroll>
      </List>
    </Container>
  );
};

const TriStateCheckbox = ({
  checked,
  indeterminate,
  onToggle,
}: {
  checked: boolean;
  indeterminate: boolean;
  onToggle: () => void;
}): JSX.Element => {
  return (
    <CheckboxLabel
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <HiddenInput
        type="checkbox"
        checked={checked}
        aria-checked={indeterminate ? "mixed" : checked}
        readOnly
      />
      <CheckboxBox
        $checked={checked}
        $indeterminate={indeterminate}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        role="button"
      >
        {indeterminate && (
          <CustomIcon name="minus" size="16px" color={ColorV2.White} />
        )}
        {!indeterminate && checked && (
          <CustomIcon name="v2/checkmark" size="16px" color={ColorV2.White} />
        )}
      </CheckboxBox>
    </CheckboxLabel>
  );
};
